import { FC, PropsWithChildren, useEffect, useState } from 'react';
import Joyride, { CallBackProps, EVENTS, STATUS } from 'react-joyride';

import { useJoyrideTourStore } from '@/store/useJoyrideTourStore.ts';
import { JoyrideTooltip } from '@/components/common/JoyrideTooltip/JoyrideTooltip.tsx';
import { calculateSpotlightOffset } from '@/providers/JoyrideProvider/utils/calculateSpotlightOffset.ts';

export interface Offset {
  x?: number;
  y?: number;
}

// Which side of the tooltip the arrow points from
export type ArrowPosition = 'top' | 'bottom' | 'left' | 'right' | 'none';

// Tooltip alignment along the edge
export type TooltipAlign = 'start' | 'center' | 'end';

export interface StepData {
  // Spotlight positioning
  spotlightOffset?: Offset;
  spotlightPadding?: number;

  // Arrow configuration (arrow auto-points to target center)
  arrowPosition?: ArrowPosition; // Override automatic arrow position
  arrowOffset?: Offset; // Fine-tune arrow position
  hideArrow?: boolean; // Hide arrow completely

  // Tooltip positioning
  tooltipOffset?: Offset; // Manual tooltip offset (px)
  tooltipWidth?: number; // Custom tooltip max width (default: 250)
  tooltipAlign?: TooltipAlign; // Align tooltip: start/center/end (works with placement)
  tooltipDistance?: number; // Distance from target in px (default: 10)

  // Tooltip style customization
  tooltipPadding?: string; // Custom padding (e.g., '8px 10px')
  tooltipBorderRadius?: number; // Border radius (default: 10)
}

export interface SpotlightOffset {
  x: number;
  y: number;
  padding: number;
}

export const JoyrideProvider: FC<PropsWithChildren> = ({ children }) => {
  const { run, stepIndex, steps, stopTour, nextStep } = useJoyrideTourStore();
  const [spotlightOffset, setSpotlightOffset] = useState<SpotlightOffset>({
    x: 0,
    y: 0,
    padding: 0
  });

  useEffect(() => {
    if (!run || !steps[stepIndex]) {
      setSpotlightOffset({ x: 0, y: 0, padding: 0 });
      return;
    }

    const target = steps[stepIndex].target;
    const stepData = (steps[stepIndex].data as StepData) || {};
    const padding = stepData.spotlightPadding ?? 5;
    const manualOffset = stepData.spotlightOffset || {};

    const timer = setTimeout(() => {
      const offset = calculateSpotlightOffset(target, padding);

      setSpotlightOffset({
        ...offset,
        x: offset.x + (manualOffset.x ?? 0),
        y: offset.y + (manualOffset.y ?? 0)
      });
    }, 0);

    return () => clearTimeout(timer);
  }, [run, stepIndex, steps]);

  const handleCallback = (data: CallBackProps) => {
    const { status, type } = data;

    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      stopTour();
      return;
    }

    // Handle step completion - go to next step (only on STEP_AFTER)
    if (type === EVENTS.STEP_AFTER) {
      nextStep();
    }
  };

  return (
    <>
      <Joyride
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        continuous={false}
        callback={handleCallback}
        tooltipComponent={JoyrideTooltip}
        disableCloseOnEsc={false}
        disableOverlayClose={false}
        disableScrolling
        disableScrollParentFix
        hideCloseButton
        hideBackButton
        showSkipButton={false}
        spotlightClicks
        spotlightPadding={spotlightOffset.padding}
        styles={{
          options: {
            zIndex: 10000,
            arrowColor: 'transparent'
          },
          spotlight: {
            borderRadius: 36,
            WebkitBorderRadius: 36,
            transform: `translate(${spotlightOffset.x}px, ${spotlightOffset.y}px)`,
            WebkitTransform: `translate(${spotlightOffset.x}px, ${spotlightOffset.y}px)`
          },
          overlay: {
            cursor: 'pointer'
          }
        }}
        floaterProps={{
          hideArrow: true,
          disableAnimation: true,
          placement: 'top'
        }}
      />
      {children}
    </>
  );
};